import { ScrollArea, Stepper } from '@tf/ui';
import type React from 'react';
import type { ReactNode } from 'react';
import { Children, useEffect, useRef } from 'react';

interface Props {
	stepsCount: number;
	step: number;
	onStepChange: (step: number) => void;
	children: ReactNode[];
	maxScrollAreaWidth?: number;
	allowNextStepsSelect?: boolean;
	style?: React.CSSProperties;
}

export const TFStepper: React.FC<Props> = ({
	step,
	onStepChange,
	children,
	maxScrollAreaWidth = Number.MAX_SAFE_INTEGER,
	style,
}) => {
	const viewport = useRef<HTMLDivElement>(null);
	const stepWidth = 210;
	const stepperWidth = Children.count(children) * stepWidth;
	const fadedBorderStyles = {
		left: { maskImage: 'linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 20%)' },
		right: { maskImage: 'linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 20%)' },
		full: {
			maskImage:
				'linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 1) 90%, rgba(0, 0, 0, 0) 100%)',
		},
	};

	const applyFades = () => {
		if (stepperWidth <= maxScrollAreaWidth) return null;

		if (step < 2) return fadedBorderStyles.right;
		if (step >= Children.count(children) - 2) return fadedBorderStyles.left;

		return fadedBorderStyles.full;
	};

	const handleStepScroll = (step: number) => {
		if (stepperWidth <= maxScrollAreaWidth || !viewport.current) {
			return;
		}

		const targetScrollLeft = Math.max(
			0,
			Math.min(step * stepWidth - maxScrollAreaWidth / 2 + stepWidth / 2, stepperWidth - maxScrollAreaWidth),
		);

		viewport.current.scrollTo({
			left: targetScrollLeft,
			behavior: 'smooth',
		});
	};

	useEffect(() => {
		handleStepScroll(step);
	}, [step, handleStepScroll]);

	return (
		<ScrollArea style={applyFades} viewportRef={viewport} scrollbars={false} maw={maxScrollAreaWidth} h="100%">
			<Stepper
				allowNextStepsSelect
				w={`${stepperWidth}px`}
				size="xs"
				onStepClick={onStepChange}
				active={step}
				style={style}
				styles={({ colors }) => ({
					stepLabel: { fontSize: '14px', lineHeight: '18px' },
					stepIcon: {
						width: '24px',
						height: '24px',
						minWidth: '24px',
						minHeight: '24px',
						background: colors.brand[1],
						color: colors.brand[6],
						borderColor: 'transparent',
						fontSize: '14px !important',
						'&[data-progress="true"]': {
							backgroundColor: colors.brand[6],
							color: colors.gray[0],
						},
						'&[data-completed="true"]': {
							backgroundColor: colors.brand[1],
							borderColor: 'transparent',
						},
					},
					step: {
						outline: 'none !important',
						padding: '8px',
						borderRadius: '4px',
						color: colors.gray[5],
						'&:hover': { backgroundColor: colors.brand[0] },
						'&[data-progress="true"]': { color: colors.gray[9] },
					},
					stepCompletedIcon: { color: colors.brand[6] },
				})}
			>
				{children}
			</Stepper>
		</ScrollArea>
	);
};
